/* eslint-disable react/prop-types */
import React, { useState } from "react";
import {
  Box,
  Typography,
  makeStyles,
  Slider,
  withStyles,
} from "@material-ui/core";
import { ArrowDropUp, ArrowDropDown } from "@material-ui/icons";
import { findIndex as _findIndex } from "lodash";

const useStyles = makeStyles((theme) => ({
  root: {
    ...theme.box,
    backgroundColor: "#fff",
    "& p": {
      fontSize: "13px",
    },
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    height: "24px",
  },
}));

const PrettoSlider = withStyles({
  root: {}, // Overriden
  thumb: {
    height: 16,
    width: 16,
    backgroundColor: "#fff",
    border: "2px solid currentColor",
    marginTop: -6,
    marginLeft: -8,
    "&:focus, &:hover, &$active": {
      boxShadow: "inherit",
    },
  },
  active: {}, // Overriden
  marked: {
    marginBottom: 0,
  }, // Overriden
  valueLabel: {
    left: "calc(-50%) -2px",
  },
  track: {
    height: 4,
    borderRadius: 2,
  },
  rail: {
    height: 4,
    borderRadius: 2,
  },
  mark: {}, // Overriden
  markLabel: {
    top: "-15px",
    color: "#4e4e4e",
  },
})(Slider);

export default function PriceSlider({
  color,
  title,
  data = {},
  onPriceSelected,
}) {
  const classes = useStyles();
  const [state, setState] = useState();

  const handleChange = (event, value) => {
    event.preventDefault();
    setState({ ...state, selectedPrice: value });
  };

  const getClosestEstimationPrice = (value, refValue, refValueIndex, marks) => {
    if (value !== refValue) return value;

    // Remove the reference value from the marks array
    const auxMarks = [...marks];
    auxMarks.splice(refValueIndex, 1);

    const diffs = auxMarks.map((mark) => ({
      diff: Math.abs(refValue - mark.value),
      value: mark.value,
    }));

    let minDiff = diffs.pop();

    diffs.forEach((item) => {
      if (item.diff < minDiff.diff) minDiff = item;
    });

    return minDiff.value;
  };

  const handleChangeCommit = (event, value) => {
    event.preventDefault();

    if (onPriceSelected)
      onPriceSelected(
        getClosestEstimationPrice(
          value,
          state?.refValue,
          state?.refValueIndex,
          state?.marks
        )
      );
  };

  React.useEffect(() => {
    const minPrice = Math.min(...data.values);
    const maxPrice = Math.max(...data.values);
    const marks = data.values.map((val) => ({ value: val }));
    // Get the index of the reference price if the value is already in the marks array
    let refValueIndex =
      data.refValue && _findIndex(marks, ["value", data.refValue]);

    if (data.refValue) {
      const referenceMark = {
        value: data.refValue,
        label: data.refValue.toFixed(1),
      };
      if (data.refValue !== minPrice && data.refValue !== maxPrice)
        if (refValueIndex !== -1) marks[refValueIndex] = referenceMark;
        else {
          marks.push(referenceMark);
          // Update the reference mark index after pushing it to the array
          refValueIndex = marks.length - 1;
        }
    }

    const initialSelectedPrice = data.refValue || data.values[0];

    if (onPriceSelected)
      onPriceSelected(
        getClosestEstimationPrice(
          initialSelectedPrice,
          data.refValue,
          refValueIndex,
          marks
        )
      );

    setState({
      minPrice,
      maxPrice,
      marks,
      refValue: data.refValue,
      refValueIndex,
      selectedPrice: initialSelectedPrice,
    });
  }, [data.values, data.refValue]);

  const sliderStyles = {
    root: {
      color: color || "primary",
    },
    mark: {
      display: "none",
    },
  };
  sliderStyles.mark[`&[data-index="${state?.refValueIndex}"]`] = {
    display: "initial",
    height: "20px",
    backgroundColor: "#8e8e8e",
    top: "5px",
    width: "1px",
  };

  const useSliderStyles = makeStyles(() => sliderStyles);

  const sliderClasses = useSliderStyles();

  return (
    <div className={classes.root}>
      {state && (
        <>
          <Box className={classes.header}>
            <Typography variant="body1">{title}</Typography>
            {state.refValue && state.selectedPrice - state.refValue !== 0 && (
              <Box display="inline-flex" alignItems="center">
                {state.selectedPrice - state.refValue > 0 && <ArrowDropUp />}
                {state.selectedPrice - state.refValue < 0 && <ArrowDropDown />}
                <Typography>
                  {(
                    (Math.abs(state.selectedPrice - state.refValue) /
                      state.refValue) *
                    100
                  ).toLocaleString("en", {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 2,
                  })}
                  %
                </Typography>
              </Box>
            )}
          </Box>
          <Box
            position="relative"
            display="flex"
            alignItems="center"
            lineHeight="0"
            marginTop="32px"
          >
            <Typography variant="body1" style={{ marginRight: "12px" }}>
              {state.minPrice.toFixed(1)}
            </Typography>
            <PrettoSlider
              value={state.selectedPrice}
              step={null}
              marks={state.marks}
              onChange={handleChange}
              valueLabelFormat={(value) => value.toFixed(1)}
              valueLabelDisplay={
                state.selectedPrice === state.refValue ? "auto" : "on"
              }
              min={state.minPrice}
              max={state.maxPrice}
              classes={{
                root: sliderClasses.root,
                mark: sliderClasses.mark,
              }}
              onChangeCommitted={handleChangeCommit}
            />
            <Typography variant="body1" style={{ marginLeft: "12px" }}>
              {state.maxPrice.toFixed(1)}
            </Typography>
          </Box>
        </>
      )}
    </div>
  );
}
